import React, { useEffect, useRef, useState } from "react";
import "./add_item_available_picking_list.css";
import { Button, Input, InputRef, notification, Space, Table, TableColumnsType, TableColumnType } from "antd";
import Highlighter from "react-highlight-words";
import { SearchOutlined } from "@ant-design/icons";
import { FilterDropdownProps } from "antd/es/table/interface";
import axios from "axios";
import useSWR from "swr";
import { SalesOrderItem } from "../../components/hoc_picking_list";

export interface DataType {
    key: React.Key;
    item_code: string;
    item_name: string;
    demand_qty: number;
    available_qty: number;
    status: string;
    uom: string;
    location_code_rack: string;
    warehouse?: string;
    supply_qty?: number;
}

type DataIndex = keyof DataType;

interface AddItemAvailableProps {
    soNo: string | undefined;
    warehouseGroup: string | undefined;
    onSelectItems: (items: DataType[]) => void;
    onCloseModal: () => void;
    pickingListItems: SalesOrderItem[];
    pickingStatusMap: Map<string, { picked: number; demand: number }>;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const AddItemAvailable: React.FC<AddItemAvailableProps> = ({ soNo, warehouseGroup, onSelectItems, onCloseModal, pickingListItems, pickingStatusMap }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [tableData, setTableData] = useState<DataType[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const fetcher = (url: string) => axios.get(url).then(res => res.data);
    const url = soNo && warehouseGroup ? `${backendUrl}/api/picking-list-available-items1?soNo=${soNo}&warehouseGroup=${warehouseGroup}` : null;
    const { data, error, isLoading } = useSWR<any[]>(url, fetcher);
    const existingPickingItemCodes = new Set(pickingListItems.map(item => item.item_code));

    useEffect(() => {
        if (data) {
            if (data.length > 0) {
                const itemsWithStatus: DataType[] = data.map((item: any, index: number) => ({
                    key: item.item_code + '-' + index,
                    item_code: item.item_code,
                    item_name: item.item_name,
                    demand_qty: item.demand_qty,
                    available_qty: item.available_qty,
                    status: item.available_qty > 0 ? 'Ready' : 'Not Ready',
                    uom: item.uom || '',
                    location_code_rack: item.location_code_rack || '',
                    warehouse: item.warehouse || '',
                    supply_qty: Number(item.supply_qty || 0),
                }));
                setTableData(itemsWithStatus);
            } else {
                setTableData([]);
            }
        } else if (error) {
            console.error("Failed to fetch available items:", error);
            notification.error({ message: "Gagal mengambil item yang tersedia dari Sales Order." });
            setTableData([]);
        } else if (!soNo) {
            setTableData([]);
        }
    }, [data, error, soNo, warehouseGroup]);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex).replace(/_/g, ' ')}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) => {
            const val = record[dataIndex];
            return val !== undefined && val !== null
                ? val.toString().toLowerCase().includes((value as string).toLowerCase())
                : false;
        },
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Item Code',
            dataIndex: 'item_code',
            key: 'item_code',
            ...getColumnSearchProps('item_code'),
        },
        {
            title: 'Item Name',
            dataIndex: 'item_name',
            key: 'item_name',
            ...getColumnSearchProps('item_name'),
        },
        {
            title: 'Demand Qty',
            dataIndex: 'demand_qty',
            key: 'demand_qty',
            ...getColumnSearchProps('demand_qty'),
            sorter: (a, b) => a.demand_qty - b.demand_qty,
        },
        {
            title: 'Available Qty',
            dataIndex: 'available_qty',
            key: 'available_qty',
            ...getColumnSearchProps('available_qty'),
            sorter: (a, b) => a.available_qty - b.available_qty,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            ...getColumnSearchProps('status'),
            render: (text, record) => (record.available_qty > 0 ? 'Ready' : 'Not Ready'),
        },
        {
            title: 'UOM',
            dataIndex: 'uom',
            key: 'uom',
            ...getColumnSearchProps('uom'),
        },
        {
            title: 'Location',
            dataIndex: 'location_code_rack',
            key: 'location_code_rack',
            ...getColumnSearchProps('location_code_rack'),
        },
    ];
    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => {
            const selectedItems = tableData.filter(item => newSelectedRowKeys.includes(item.key));
            const uniqueKeys: React.Key[] = [];
            const seenCodes = new Set<string>();
            let hasDup = false;
            selectedItems.forEach(item => {
                if (seenCodes.has(item.item_code)) {
                    hasDup = true;
                } else {
                    seenCodes.add(item.item_code);
                    uniqueKeys.push(item.key);
                }
            });
            if (hasDup) {
                notification.warning({
                    message: "Pilihan Dibatalkan",
                    description: "Kode item yang sama tidak boleh dipilih lebih dari sekali."
                });
            }
            setSelectedRowKeys(uniqueKeys);
        },
        getCheckboxProps: (record: DataType) => {
            const isInPickingList = existingPickingItemCodes.has(record.item_code);
            const isFullySupplied = Number(record.supply_qty || 0) === Number(record.demand_qty || 0);
            const isInsufficientStock = Number(record.available_qty || 0) < Number(record.demand_qty || 0);
            return {
                disabled: isInPickingList || isFullySupplied || isInsufficientStock,
                name: record.item_code,
            };
        },
    };

    const handleOkButtonClick = () => {
        const selectedItems = tableData.filter(item => selectedRowKeys.includes(item.key));
        if (selectedItems.length === 0) {
            notification.warning({ message: "Pilih setidaknya satu item untuk ditambahkan." });
            return;
        }

        const existingItemCodes = new Set(pickingListItems.map(item => item.item_code));
        const duplicateItems: string[] = [];
        const localSeenCodes = new Set<string>();

        for (const selectedItem of selectedItems) {
            const isFullySupplied = Number(selectedItem.supply_qty || 0) === Number(selectedItem.demand_qty || 0);

            if (existingItemCodes.has(selectedItem.item_code) || isFullySupplied || localSeenCodes.has(selectedItem.item_code)) {
                duplicateItems.push(selectedItem.item_code);
            }
            localSeenCodes.add(selectedItem.item_code);
        }

        if (duplicateItems.length > 0) {
            notification.warning({
                message: "Item sudah tersedia",
                description: `Item dengan kode berikut sudah ada di daftar picking atau dipilih ganda: ${[...new Set(duplicateItems)].join(', ')}.`,
                duration: 5
            });
            return;
        }

        onSelectItems(selectedItems);
        onCloseModal();
    };

    return (

        <div id="iou6k" style={transformStyle}>
            <div className="gjs-grid-row" id="igosg">
                <div className="gjs-grid-column" id="itohu">
                    <Table<DataType>
                        rowSelection={rowSelection}
                        columns={columns}
                        dataSource={tableData}
                        scroll={{ x: 'max-content' }}
                        rowClassName={(record) => {
                            const isInPickingList = existingPickingItemCodes.has(record.item_code);
                            const isFullySupplied = Number(record.supply_qty || 0) === Number(record.demand_qty || 0);
                            const isInsufficientStock = Number(record.available_qty || 0) < Number(record.demand_qty || 0);
                            return (isInPickingList || isFullySupplied || isInsufficientStock) ? 'dimmed-row' : '';
                        }}
                    />
                </div>
            </div>
            <Space>
                <Button
                    type="primary"
                    id="add_item_picking"
                    style={{
                        height: "40px",
                        width: "105px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                    icon={
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            style={{ width: "22px", height: "22px" }}
                        >
                            <path
                                fillRule="evenodd"
                                d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 9a.75.75 0 0 0-1.5 0v2.25H9a.75.75 0 0 0 0 1.5h2.25V15a.75.75 0 0 0 1.5 0v-2.25H15a.75.75 0 0 0 0-1.5h-2.25V9Z"
                                clipRule="evenodd"
                            />
                        </svg>
                    }
                    onClick={handleOkButtonClick}
                >
                    OK
                </Button>
            </Space>
        </div>
    );
};

export default AddItemAvailable;
